import { DestroyRef, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import {
  Column,
  FilterChangedEvent,
  FilterModel,
  GridApi,
  PaginationChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { isEmpty as _isEmpty } from 'lodash/fp'

import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  Subscription,
  delay,
  fromEvent,
  switchMap,
  timer,
} from 'rxjs'
import { catchError, filter, map, take, takeUntil, tap } from 'rxjs/operators'

import {
  BULK_UPDATE_DELAY,
  DEFAULT_PAGE_SIZE,
  ExecuteTaskActions,
  PaginatedResponse,
  RESET_HISTORY_TASK_ACTIONS,
  SseMessageType,
  SseTaskMessageType,
  TASK_ACTION_LABELS,
  TaskActions,
  TaskComment,
  TaskHistory,
  TaskManagerAnalytics,
  User,
  ViewColumn,
  ViewTask,
  ViewTasksFilter,
  ViewTasksSort,
  getProcessUrl,
  localizeString,
  sortedColumnState,
  toViewTasksSorts,
} from '@flowx/core-sdk'

import { PublicTasksService } from './public-tasks.service'
import { TasksService } from './tasks.service'

import { TASK_EVENTS_UPDATE_DELAY } from '../task.constants'
import { GET_EMPTY_DATA_SOURCE, MAP_FILTERS } from '../task.helpers'

type EventData = Record<string, any>

@Injectable()
export class ViewTasksStateService {
  #publicTasksService = inject(PublicTasksService)
  #tasksService = inject(TasksService)
  #destroyRef = inject(DestroyRef)

  #pageSize: number = DEFAULT_PAGE_SIZE
  private _taskLoading = new BehaviorSubject<boolean>(false)

  // COMMENTS
  private taskComments$ = new BehaviorSubject<TaskComment[]>([])
  private loadingTaskComments$ = new BehaviorSubject<boolean>(false)

  // HISTORY
  private taskHistory$ = new BehaviorSubject<TaskHistory[]>([])
  private loadingTaskHistory$ = new BehaviorSubject<boolean>(false)

  // ASSIGNEE
  private assignees$ = new BehaviorSubject<User[]>([])
  private assigneesError$ = new BehaviorSubject<string>(null)
  private assigneesLoading$ = new BehaviorSubject<boolean>(false)
  private _selectedTask$ = new BehaviorSubject<ViewTask>(null)
  private selectedTaskId: ViewTask['id']
  private _triggerredActionKey: TaskActions = null

  private _gridTasks: Partial<ViewTask>[] = []
  private _updatedTasks: string[] = []

  private _currentPage = 0
  private _loadedPages = []
  private _gridTasks$ = new Subject<Partial<ViewTask>[]>()
  private _loadingTasks = new BehaviorSubject<boolean>(false)

  private _filters: ViewTasksFilter[] = []
  private _sort: ViewTasksSort[] = []

  private pageVisibilityChangeSubscription: Subscription = null
  private _timerSet = false
  private _timer: Observable<number>

  private _keyword = ''
  private killUpdateTasks$ = new BehaviorSubject<boolean>(false)
  /** Set by the task manager; undefined for a standalone one, which sends no events. */
  analytics?: TaskManagerAnalytics

  executeTaskAction(action: ExecuteTaskActions, task: ViewTask, payload?: any): Observable<void> {
    return this.#publicTasksService.executeTaskAction(action, task, payload)
  }

  getTaskComments(taskId: ViewTask['id']): void {
    this.loadingTaskComments$.next(true)
    this.#publicTasksService
      .getTaskComments(taskId)
      .pipe(
        tap((taskComments) => {
          this.taskComments$.next([...this.taskComments$.value, ...taskComments])
          this.loadingTaskComments$.next(false)
        })
      )
      .subscribe()
  }

  getTaskHistory(taskId: ViewTask['id']): void {
    this.loadingTaskHistory$.next(true)
    this.#publicTasksService
      .getTaskHistory(taskId)
      .pipe(
        tap((taskHistory) => {
          this.taskHistory$.next([...this.taskHistory$.value, ...taskHistory])
          this.loadingTaskHistory$.next(false)
        })
      )
      .subscribe()
  }

  addComment(taskId: ViewTask['id'], comment: { message: string }): Observable<TaskComment> {
    return this.#publicTasksService.addComment(taskId, comment).pipe(
      tap((taskComment) => {
        this.taskComments$.next([taskComment, ...this.taskComments$.value])
      })
    )
  }

  deleteComment(taskId: ViewTask['id'], commentId: TaskComment['commentId']): Observable<void> {
    return this.#publicTasksService.deleteComment(taskId, commentId).pipe(
      tap(() => {
        this.taskComments$.next(
          this.taskComments$.value.filter((_comment) => _comment.commentId !== commentId)
        )
      })
    )
  }

  getAssignees(task: ViewTask, query: { keyword: string }): Observable<User[]> {
    this.assigneesLoading$.next(true)
    return this.#publicTasksService.getAssignees(task, query).pipe(
      take(1),
      map((response: User[]) => {
        this.assigneesLoading$.next(false)
        this.assignees$.next(response)
        return response
      }),
      catchError((err: any) => {
        if (err.error.status === 404) {
          this.assigneesError$.next('Nothing found!')
          this.assignees$.next([])
        } else {
          this.assigneesError$.next('Something went wrong!')
        }
        this.assigneesLoading$.next(false)
        return EMPTY
      })
    )
  }

  fetchBulkAssignees(
    tasks: { id: ViewTask['id']; tokenUuid: string }[],
    query: { keyword: string }
  ): Observable<User[]> {
    return this.#publicTasksService.getBulkAssignees(tasks, query)
  }

  bulkUpdate(
    tasks: { id: ViewTask['id']; tokenUuid: string }[],
    action: ExecuteTaskActions,
    username: string
  ): Observable<PaginatedResponse<ViewTask>> {
    this.initSse()
    return this.#publicTasksService.bulkUpdate(tasks, action, username).pipe(
      delay(BULK_UPDATE_DELAY),
      switchMap(() => {
        this._loadedPages = []
        return this.getViewTasks(this._currentPage)
      })
    )
  }

  resetTaskComments(): void {
    this.taskComments$.next([])
  }

  resetTaskHistory(): void {
    this.taskHistory$.next([])
  }

  get taskComments(): Observable<TaskComment[]> {
    return this.taskComments$.asObservable()
  }

  get taskCommentsLoading(): Observable<boolean> {
    return this.loadingTaskComments$.asObservable()
  }

  get taskHistory(): Observable<TaskHistory[]> {
    return this.taskHistory$.asObservable()
  }

  get taskHistoryLoading(): Observable<boolean> {
    return this.loadingTaskHistory$.asObservable()
  }

  get assignees(): Observable<User[]> {
    return this.assignees$.asObservable()
  }

  get assigneeError(): Observable<string> {
    return this.assigneesError$.asObservable()
  }

  get assigneeLoading(): Observable<boolean> {
    return this.assigneesLoading$.asObservable()
  }

  resetAssignees(): void {
    this.assigneesLoading$.next(false)
    this.assignees$.next([])
    this.assigneesError$.next(null)
  }

  setSelectedTask(task: ViewTask): void {
    this.selectedTaskId = task ? task.id : null
    this._selectedTask$.next(task)
  }

  get selectedTask(): Observable<ViewTask> {
    return this._selectedTask$.asObservable()
  }

  getViewTasks(page: number): Observable<PaginatedResponse<ViewTask>> {
    this._loadingTasks.next(true)
    return this.#tasksService
      .getViewTasks(page, this._sort, this._filters, this._keyword, this.#pageSize)
      .pipe(
        tap((response) => {
          this._loadingTasks.next(false)

          if (!this._loadedPages.length) {
            this._gridTasks = GET_EMPTY_DATA_SOURCE(response.totalElements)
          }

          this.setGridDataSource(page, response.content)

          this._loadedPages.push(page)
          this._gridTasks$.next(this._gridTasks)
          this.analytics?.loaded(response.totalElements)
        })
      )
  }

  resetTasks(): void {
    this._gridTasks = []
    this._currentPage = 0
    this._filters = []
    this._sort = []
    this._updatedTasks = []
    this._keyword = ''
    this.killUpdateTasks$.next(false)
  }

  setCurrentPage(pageNumber: number): void {
    this._currentPage = pageNumber
  }

  get gridTasks(): Observable<Partial<ViewTask>[]> {
    return this._gridTasks$.asObservable()
  }

  get loadedPages(): number[] {
    return this._loadedPages
  }

  updatePriority(task: ViewTask, priority: number): Observable<void> {
    return this.#publicTasksService.updatePriority(task.id, priority).pipe(
      tap(() => {
        this.getUpdatedTasks()
      })
    )
  }

  setGridDataSource(page: number, tasks: Partial<ViewTask>[]): void {
    this._gridTasks.splice(page * this.#pageSize, tasks.length, ...tasks)
  }

  setSort(api: GridApi): void {
    this._loadedPages = []
    this._sort = toViewTasksSorts(sortedColumnState(api))
  }

  setFilters(params: FilterModel, columns: ViewColumn[]): void {
    this._loadedPages = []
    this._filters = MAP_FILTERS(params, columns)
  }

  onSortChanged(event: SortChangedEvent): void {
    this.setSort(event.api)
    // A column defs update (a view change) makes the grid re-fire sortChanged.
    if (event.source !== 'gridOptionsChanged') {
      const sortedColumn =
        event.columns.find((column: Column) => column.getSort()) ?? event.columns[0]
      this.analytics?.sorted(
        sortedColumn?.getColDef().headerName,
        sortedColumn?.getSort() as 'asc' | 'desc' | null | undefined
      )
    }
    this.getViewTasks(event.api.paginationGetCurrentPage()).subscribe()
  }

  onFiltersChanged(event: FilterChangedEvent): void {
    this.setFilters(event.api.getFilterModel(), this.#publicTasksService.getView().columns)
    this.analytics?.filtered(
      event.api.getFilterModel(),
      (key) => event.api.getColumn(key)?.getColDef().headerName
    )
    if (event.api.paginationGetCurrentPage()) {
      event.api.paginationGoToFirstPage()
    } else {
      this.getViewTasks(0).subscribe()
    }
  }

  onPageSizeChanged(event: PaginationChangedEvent): void {
    this.#pageSize = event.api.paginationGetPageSize()
    if (event.api.paginationGetCurrentPage()) {
      event.api.paginationGoToFirstPage()
    } else {
      this.getViewTasks(0).subscribe()
    }
  }

  initSse(): void {
    this.#publicTasksService.initSse()

    this.#publicTasksService
      .getSseEvents()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event) => {
        this.handleSseEvent({ event })
      })

    this.pageVisibilityChangeSubscription?.unsubscribe()
    this.pageVisibilityChangeSubscription =
      this.setupSseReconnectOnPageVisibilityChange().subscribe()
  }

  handleSseEvent({ event }: { event: MessageEvent<string> }): void {
    const eventData: EventData = !_isEmpty(event.data) ? JSON.parse(event.data) : null

    switch (event.type) {
      case SseMessageType.Data:
        if (eventData?.eventType === SseTaskMessageType.VIEW_UPDATED) {
          if (!this.killUpdateTasks$.getValue()) {
            this.setGetUpdatedTasksDelay(eventData)
          }

          if (eventData.taskId === this.selectedTaskId) {
            this.updateSelectedTask(this._selectedTask$.value).pipe(take(1)).subscribe()
            this.getUpdatedTasks()
          }
        }
        break
    }
  }

  setGetUpdatedTasksDelay(eventData: EventData): void {
    if (!this._updatedTasks.includes(eventData.taskId)) {
      this._updatedTasks.push(eventData.taskId)
    }

    if (!this._timerSet) {
      this._timerSet = true
      this._timer = timer(TASK_EVENTS_UPDATE_DELAY)
      this._timer
        .pipe(takeUntil(this.killUpdateTasks$.pipe(filter((value) => value))))
        .subscribe(() => {
          this.getUpdatedTasks()
        })
    }
  }

  updateSelectedTask(task: ViewTask): Observable<ViewTask> {
    return this.#publicTasksService.getViewTask(task.id, task.tokenUuid).pipe(
      tap((updatedTask) => {
        this.setSelectedTask(updatedTask)
        if (
          this._triggerredActionKey &&
          RESET_HISTORY_TASK_ACTIONS.includes(this._triggerredActionKey)
        ) {
          this.resetTaskHistory()
          this.getTaskHistory(task.id)
          this._triggerredActionKey = null
        }
      })
    )
  }

  getUpdatedTasks(): void {
    this._loadedPages = []
    this._loadingTasks
      .pipe(
        filter((value) => !value),
        take(1),
        switchMap(() => this.getViewTasks(this._currentPage))
      )
      .subscribe(() => {
        this._timerSet = false
        this._updatedTasks = []
      })
  }

  search(api: GridApi, keyword: string): void {
    if (api) {
      this._keyword = keyword
      this._loadedPages = []
      if (api.paginationGetCurrentPage()) {
        api.paginationGoToFirstPage()
      } else {
        this.getViewTasks(0).subscribe()
      }
    }
  }

  openProcess(task: ViewTask): void {
    if (task.baseUrl) {
      this.analytics?.taskActioned(TaskActions.VIEW, task.processInstance?.processState)
      window.open(
        getProcessUrl(
          task.baseUrl,
          task.appId,
          task.processInstance.contextInstanceUuid
            ? task.processInstance.contextInstanceUuid
            : task.processInstance.processInstanceUuid
        ),
        '_blank'
      )
    }
  }

  showViewAction(task: ViewTask): boolean {
    return (
      task.possibleActions.includes(TaskActions.EXECUTE) ||
      task.possibleActions.includes(TaskActions.VIEW)
    )
  }

  buildTaskActions(
    task: ViewTask
  ): Record<string, { label: string; onClick: (payload?: any) => object }> {
    const viewAction = {
      label: TASK_ACTION_LABELS[TaskActions.VIEW],
      value: TaskActions.VIEW,
      onClick: (_task: ViewTask) => this.openProcess(_task),
    }
    return [
      ...(this.showViewAction(task) ? [viewAction] : []),
      ...Object.keys(TaskActions)
        .filter(
          (key: TaskActions) => task.possibleActions.includes(key) && key !== TaskActions.VIEW
        )
        .map((key: ExecuteTaskActions) => ({
          label: TASK_ACTION_LABELS[key],
          value: key,
          onClick: (payload?: any) => {
            this.executeTaskAction(key, task, payload || null)
              .pipe(take(1))
              .subscribe(() => {
                this._triggerredActionKey = key
                this.analytics?.taskActioned(key, task.processInstance?.processState)
              })
          },
        })),
    ].reduce((acc, action) => {
      acc[action.value] = { label: localizeString(action.label), onClick: action.onClick }
      return acc
    }, {})
  }

  setKillUpdateTasks(value: boolean): void {
    this.killUpdateTasks$.next(value)
  }

  closeSseConnection(): void {
    this._updatedTasks = []
    this.killUpdateTasks$.next(false)
    this.#publicTasksService.closeSseConnection()
    this.pageVisibilityChangeSubscription?.unsubscribe()
  }

  private setupSseReconnectOnPageVisibilityChange(): Observable<Document | Event> {
    return fromEvent(document, 'visibilitychange').pipe(
      tap(() => {
        if (document.visibilityState === 'hidden') {
          this.#publicTasksService.closeSseConnection()
        } else if (document.visibilityState === 'visible') {
          this.initSse()
          // SSE was closed while the tab was hidden, so any VIEW_UPDATED events
          // from that window are lost — reload the current page to catch up.
          if (!this.killUpdateTasks$.getValue()) {
            this.getUpdatedTasks()
          }
        }
      })
    )
  }
}
